import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI, interviewAPI, quizAPI, submissionAPI, assignmentAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function StudentProfile() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [student, setStudent] = useState(null);
  const [quizSubmissions, setQuizSubmissions] = useState([]);
  const [interviewResponses, setInterviewResponses] = useState([]);
  const [codingSubmissions, setCodingSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [sendingAccountDetails, setSendingAccountDetails] = useState(false);
  const [performanceStats, setPerformanceStats] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/students');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      // Fetch student details
      const studentResponse = await authAPI.getStudent(studentId);
      setStudent(studentResponse.data.student);

      // Fetch student's quiz submissions with full details
      const performanceResponse = await authAPI.getStudentPerformance(studentId);
      const performanceData = performanceResponse.data.performance || [];
      
      // Store performance stats
      setPerformanceStats(performanceResponse.data.stats || null);
      
      // Fetch full submission details for each quiz
      const submissionsWithDetails = await Promise.all(
        performanceData.map(async (perf) => {
          try {
            const submissionResponse = await submissionAPI.getStudentAttempts(studentId, perf.quizId);
            // Get the specific attempt that matches this performance record
            const studentSubmission = submissionResponse.data.find(sub => 
              sub._id === perf.submissionId
            );
            return {
              ...perf,
              submissionDetails: studentSubmission || null
            };
          } catch (err) {
            console.error('Error fetching submission for quiz:', perf.quizId, err);
            return {
              ...perf,
              submissionDetails: null
            };
          }
        })
      );
      
      setQuizSubmissions(submissionsWithDetails);

      // Fetch student's interview responses
      const interviewsResponse = await interviewAPI.getInterviews(1, 1000);
      const allInterviews = interviewsResponse.data.interviews || [];
      
      // Filter interviews assigned to this student and get ALL their responses (all attempts)
      const studentInterviews = [];
      for (const interview of allInterviews) {
        if (interview.assignedTo && interview.assignedTo.some(assigned => 
          (typeof assigned === 'string' ? assigned : assigned._id) === studentId
        )) {
          try {
            const responsesData = await interviewAPI.getResponses(interview._id);
            // Get ALL student responses (all attempts), not just one
            const studentResponses = responsesData.data.responses.filter(r => 
              (typeof r.student === 'string' ? r.student : r.student._id) === studentId
            );
            // Add each attempt as a separate entry
            studentResponses.forEach(response => {
              studentInterviews.push({
                ...interview,
                response: response
              });
            });
          } catch (err) {
            console.error('Error fetching interview responses:', err);
          }
        }
      }
      // Group responses by interview ID to show all attempts together
      const interviewsMap = new Map();
      studentInterviews.forEach(item => {
        const interviewId = item._id;
        if (!interviewsMap.has(interviewId)) {
          interviewsMap.set(interviewId, {
            interview: item,
            attempts: []
          });
        }
        interviewsMap.get(interviewId).attempts.push(item.response);
      });
      
      // Convert map to array and sort attempts by attempt number
      const groupedInterviews = Array.from(interviewsMap.values()).map(group => {
        group.attempts.sort((a, b) => (a.attemptNumber || 1) - (b.attemptNumber || 1));
        return group;
      });
      
      setInterviewResponses(groupedInterviews);

      // Fetch student's coding assignment submissions
      try {
        const codingResponse = await assignmentAPI.getStudentSubmissions(studentId);
        setCodingSubmissions(codingResponse.data.submissions || []);
      } catch (err) {
        console.error('Error fetching coding submissions:', err);
        setCodingSubmissions([]);
      }

    } catch (error) {
      console.error('Error fetching student data:', error);
      showToast(error.response?.data?.message || 'Failed to fetch student data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#48bb78';
    if (percentage >= 60) return '#f6ad55';
    return '#fc8181';
  };

  const fetchSubmissionDetails = async (quizId) => {
    if (expandedSubmission === quizId) {
      // Toggle off if already expanded
      setExpandedSubmission(null);
      return;
    }

    // Find the submission in our already loaded data
    const submission = quizSubmissions.find(s => s.quizId === quizId);
    if (submission && submission.submissionDetails) {
      setExpandedSubmission(quizId);
    } else {
      showToast('Submission details not available', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'submitted': { color: '#48bb78', bg: '#c6f6d5', text: 'Submitted' },
      'in_progress': { color: '#f6ad55', bg: '#feebc8', text: 'In Progress' },
      'not_started': { color: '#a0aec0', bg: '#e2e8f0', text: 'Not Started' },
      'timed_out': { color: '#fc8181', bg: '#fed7d7', text: 'Timed Out' }
    };
    const config = statusMap[status] || statusMap['not_started'];
    return (
      <span style={{
        padding: '4px 10px',
        backgroundColor: config.bg,
        color: config.color,
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600'
      }}>
        {config.text}
      </span>
    );
  };

  const handleToggleUserStatus = async () => {
    if (!student) return;
    
    const action = student.isActive === false ? 'activate' : 'deactivate';
    const confirmMessage = `Are you sure you want to ${action} ${student.name} (${student.email})?\n\n${student.isActive !== false ? 'The user will no longer be able to log in.' : 'The user will be able to log in again.'}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTogglingStatus(true);
    try {
      const response = await authAPI.toggleUserStatus(student._id);
      showToast(response.data.message || `User ${action}d successfully`, 'success');
      // Refresh student data
      fetchStudentData();
    } catch (error) {
      showToast(error.response?.data?.message || `Failed to ${action} user`, 'error');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!student) return;
    
    const confirmMessage = `Are you sure you want to delete ${student.name} (${student.email})?\n\nThis will permanently delete:\n• All quiz submissions\n• All interview responses\n• All resumes\n• Remove from all groups\n• All other associated data\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await authAPI.deleteUser(student._id);
      showToast(response.data.message || 'User deleted successfully', 'success');
      // Navigate back to students list
      navigate('/students');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete user', 'error');
      setDeleting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!student) return;

    try {
      const response = await authAPI.resetStudentPassword(student._id);
      const newPassword = response.data.newPassword;
      
      // Show success message with the new password
      const message = `Password reset successful!\n\nNew temporary password: ${newPassword}\n\nThe student has been notified via email at ${student.email}`;
      alert(message);
      
      showToast('Password reset successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reset password', 'error');
    }
  };

  const handleSendAccountDetails = async () => {
    if (!student) return;

    setSendingAccountDetails(true);
    try {
      const response = await authAPI.sendAccountDetails(student._id);
      showToast(response.data.message || 'Account details sent successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to send account details', 'error');
    } finally {
      setSendingAccountDetails(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading student profile...</div>;
  }

  if (!student) {
    return (
      <div className="container">
        <div className="card">
          <p>Student not found</p>
          <button onClick={() => navigate('/students')} className="btn btn-primary">
            Back to Students
          </button>
        </div>
      </div>
    );
  }

  const quizStats = {
    total: quizSubmissions.filter(s => s.attemptNumber === 1).length,
    avgScore: quizSubmissions.filter(s => s.attemptNumber === 1).length > 0
      ? (quizSubmissions.filter(s => s.attemptNumber === 1).reduce((sum, s) => sum + parseFloat(s.percentage), 0) / quizSubmissions.filter(s => s.attemptNumber === 1).length).toFixed(1)
      : 0,
    bestScore: quizSubmissions.length > 0
      ? Math.max(...quizSubmissions.map(s => parseFloat(s.percentage))).toFixed(1)
      : 0
  };

  const interviewStats = {
    total: interviewResponses.length,
    completed: interviewResponses.filter(g => g.attempts.some(a => a.status === 'submitted')).length,
    inProgress: interviewResponses.filter(g => g.attempts.some(a => a.status === 'in_progress')).length,
    notStarted: interviewResponses.filter(g => g.attempts.every(a => a.status === 'not_started')).length
  };

  const assignmentStats = {
    total: codingSubmissions.length,
    completed: codingSubmissions.filter(s => s.isCompleted).length,
    incomplete: codingSubmissions.filter(s => !s.isCompleted).length
  };

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/students')}
            style={{
              padding: '6px 12px',
              background: '#f7fafc',
              color: '#667eea',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            ← Back to Students
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: '700',
                color: 'white',
                textTransform: 'uppercase'
              }}>
                {student.name.charAt(0)}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <h2 style={{ color: '#2d3748', fontSize: '24px', margin: 0, textTransform: 'capitalize' }}>
                    {student.name}
                  </h2>
                  {student.isActive === false && (
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      border: '1px solid #fecaca'
                    }}>
                      Deactivated
                    </span>
                  )}
                </div>
                <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
                  {student.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ fontSize: '13px', marginBottom: '8px', opacity: 0.9 }}>Quizzes Taken</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>{quizStats.total}</div>
          </div>
          
          <div style={{
            padding: '20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', marginBottom: '8px', color: '#718096' }}>Average Score</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: getScoreColor(quizStats.avgScore) }}>
              {quizStats.avgScore}%
            </div>
          </div>
          
          <div style={{
            padding: '20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', marginBottom: '8px', color: '#718096' }}>Best Score</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#48bb78' }}>
              {quizStats.bestScore}%
            </div>
          </div>
          
          <div style={{
            padding: '20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', marginBottom: '8px', color: '#718096' }}>Interviews</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>
              {interviewStats.completed}/{interviewStats.total}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '12px'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '8px 16px',
              background: activeTab === 'overview' ? '#667eea' : 'white',
              color: activeTab === 'overview' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'overview' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📊 Overview
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            style={{
              padding: '8px 16px',
              background: activeTab === 'quizzes' ? '#667eea' : 'white',
              color: activeTab === 'quizzes' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'quizzes' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📝 Quizzes ({quizStats.total})
          </button>          <button
            onClick={() => setActiveTab('performance')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'performance' ? '#667eea' : 'white',
              color: activeTab === 'performance' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'performance' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📊 Performance Trends
          </button>          <button
            onClick={() => setActiveTab('interviews')}
            style={{
              padding: '8px 16px',
              background: activeTab === 'interviews' ? '#667eea' : 'white',
              color: activeTab === 'interviews' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'interviews' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🎤 Interviews ({interviewStats.total})
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            style={{
              padding: '8px 16px',
              background: activeTab === 'assignments' ? '#667eea' : 'white',
              color: activeTab === 'assignments' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'assignments' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💻 Assignments ({assignmentStats.total})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            style={{
              padding: '8px 16px',
              background: activeTab === 'profile' ? '#667eea' : 'white',
              color: activeTab === 'profile' ? 'white' : '#667eea',
              border: `1px solid ${activeTab === 'profile' ? '#667eea' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            👤 Profile
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Student Information
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Registered On</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  {new Date(student.createdAt).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              
              <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Groups</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  {student.groups && student.groups.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                      {student.groups.map(group => (
                        <span
                          key={group._id}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#eef2ff',
                            color: '#667eea',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            border: '1px solid #c7d2fe'
                          }}
                        >
                          {group.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    'No groups assigned'
                  )}
                </div>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div style={{ padding: '20px', background: '#f0fff4', borderRadius: '12px', border: '1px solid #9ae6b4' }}>
                <div style={{ fontSize: '13px', color: '#2f855a', marginBottom: '12px', fontWeight: '600' }}>
                  Quiz Performance
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#276749' }}>Total Quizzes</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2f855a' }}>{quizStats.total}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#276749' }}>Average</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2f855a' }}>{quizStats.avgScore}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#276749' }}>Best</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2f855a' }}>{quizStats.bestScore}%</span>
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px', background: '#ebf8ff', borderRadius: '12px', border: '1px solid #90cdf4' }}>
                <div style={{ fontSize: '13px', color: '#2c5282', marginBottom: '12px', fontWeight: '600' }}>
                  Interview Progress
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#2c5282' }}>Completed</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2b6cb0' }}>{interviewStats.completed}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#2c5282' }}>In Progress</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2b6cb0' }}>{interviewStats.inProgress}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', color: '#2c5282' }}>Not Started</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2b6cb0' }}>{interviewStats.notStarted}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'quizzes' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Quiz Submissions
            </h3>
            
            {quizSubmissions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: '#f7fafc',
                borderRadius: '12px',
                color: '#718096'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                <p>No quiz submissions yet</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Quiz
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Score
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Percentage
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Rank
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Date
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizSubmissions.map((submission, index) => (
                      <React.Fragment key={index}>
                        <tr
                          style={{
                            borderBottom: expandedSubmission === submission.quizId ? 'none' : '1px solid #e2e8f0',
                            transition: 'background-color 0.2s',
                            backgroundColor: expandedSubmission === submission.quizId ? '#f7fafc' : 'white'
                          }}
                          onMouseEnter={(e) => {
                            if (expandedSubmission !== submission.quizId) {
                              e.currentTarget.style.backgroundColor = '#f7fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (expandedSubmission !== submission.quizId) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <td style={{ padding: '12px', fontSize: '13px', color: '#2d3748' }}>
                            <div style={{ fontWeight: '600' }}>{submission.quizTitle}</div>
                            {submission.attemptNumber > 1 && (
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                background: '#feebc8',
                                color: '#744210',
                                borderRadius: '4px',
                                marginTop: '4px',
                                display: 'inline-block'
                              }}>
                                PRACTICE
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#2d3748', fontWeight: '600' }}>
                            {submission.score}/{submission.totalQuestions}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '700',
                              color: getScoreColor(submission.percentage),
                              background: `${getScoreColor(submission.percentage)}20`
                            }}>
                              {submission.percentage}%
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#718096' }}>
                            {submission.rank ? `#${submission.rank}` : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#718096' }}>
                            {new Date(submission.submittedAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <button
                              onClick={() => fetchSubmissionDetails(submission.quizId)}
                              style={{
                                padding: '6px 12px',
                                background: expandedSubmission === submission.quizId ? '#667eea' : 'white',
                                color: expandedSubmission === submission.quizId ? 'white' : '#667eea',
                                border: '1px solid #667eea',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              {expandedSubmission === submission.quizId ? '▼ Hide Details' : '▶ View Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedSubmission === submission.quizId && submission.submissionDetails && (
                          <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td colSpan="6" style={{ padding: '16px', backgroundColor: '#f7fafc' }}>
                              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#667eea', marginBottom: '12px' }}>
                                  Submission Details
                                </h4>
                                {submission.submissionDetails.answers && submission.submissionDetails.answers.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {submission.submissionDetails.answers.map((answer, ansIdx) => (
                                      <div key={ansIdx} style={{
                                        padding: '12px',
                                        background: '#f7fafc',
                                        borderRadius: '6px',
                                        borderLeft: `4px solid ${answer.isCorrect ? '#48bb78' : '#fc8181'}`
                                      }}>
                                        <div style={{ marginBottom: '8px' }}>
                                          <strong style={{ fontSize: '13px', color: '#2d3748' }}>
                                            Q{ansIdx + 1}: {answer.question?.question || 'Question not available'}
                                          </strong>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                                          <strong>Your Answer:</strong> {answer.answer || 'Not answered'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: answer.isCorrect ? '#2f855a' : '#c53030', fontWeight: '600' }}>
                                          {answer.isCorrect ? '✓ Correct' : `✗ Incorrect (Correct: ${answer.question?.correctAnswer || 'N/A'})`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ color: '#718096', fontSize: '13px' }}>No answers available</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'performance' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Performance Trends
            </h3>
            
            {performanceStats ? (
              <>
                {/* Stats Summary */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#eef2ff',
                    borderRadius: '10px',
                    border: '1.5px solid #667eea'
                  }}>
                    <div style={{ fontSize: '12px', color: '#667eea', fontWeight: '600', marginBottom: '4px' }}>
                      Total Quizzes
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
                      {performanceStats.totalQuizzes}
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f0fff4',
                    borderRadius: '10px',
                    border: '1.5px solid #48bb78'
                  }}>
                    <div style={{ fontSize: '12px', color: '#2f855a', fontWeight: '600', marginBottom: '4px' }}>
                      Average Score
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
                      {performanceStats.averagePercentage}%
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fffbeb',
                    borderRadius: '10px',
                    border: '1.5px solid #f59e0b'
                  }}>
                    <div style={{ fontSize: '12px', color: '#b45309', fontWeight: '600', marginBottom: '4px' }}>
                      Best Score
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
                      {performanceStats.bestPerformance}%
                    </div>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '10px',
                    border: '1.5px solid #ef4444'
                  }}>
                    <div style={{ fontSize: '12px', color: '#b91c1c', fontWeight: '600', marginBottom: '4px' }}>
                      Lowest Score
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
                      {performanceStats.worstPerformance}%
                    </div>
                  </div>
                </div>

                {/* Performance Timeline */}
                <div style={{
                  backgroundColor: '#f7fafc',
                  borderRadius: '10px',
                  padding: '20px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                    Quiz History
                  </h4>
                  {quizSubmissions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      No quiz submissions yet
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {quizSubmissions.map((perf, index) => {
                        const percentage = parseFloat(perf.percentage);
                        const barColor = percentage >= 75 ? '#48bb78' : percentage >= 50 ? '#f59e0b' : '#ef4444';
                        
                        return (
                          <div key={`${perf.quizId}-${index}`} style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '16px',
                            border: perf.attemptNumber > 1 ? '1px dashed #cbd5e0' : '1px solid #e2e8f0',
                            opacity: perf.attemptNumber > 1 ? 0.7 : 1
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '14px', marginBottom: '4px' }}>
                                  {index + 1}. {perf.quizTitle}
                                  {perf.attemptNumber > 1 && (
                                    <span style={{ 
                                      marginLeft: '8px', 
                                      padding: '2px 8px', 
                                      backgroundColor: '#fef3c7', 
                                      color: '#92400e',
                                      borderRadius: '4px', 
                                      fontSize: '11px',
                                      fontWeight: '600'
                                    }}>
                                      PRACTICE
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096' }}>
                                  {new Date(perf.submittedAt).toLocaleDateString('en-US', { 
                                    weekday: 'short',
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ 
                                  fontSize: '18px', 
                                  fontWeight: '700',
                                  color: barColor
                                }}>
                                  {percentage}%
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096' }}>
                                  {perf.score}/{perf.totalQuestions}
                                </div>
                              </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div style={{
                              width: '100%',
                              height: '8px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${percentage}%`,
                                height: '100%',
                                backgroundColor: barColor,
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                No performance data available
              </div>
            )}
          </div>
        )}

        {activeTab === 'interviews' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Interview Responses
            </h3>
            
            {interviewResponses.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: '#f7fafc',
                borderRadius: '12px',
                color: '#718096'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎤</div>
                <p>No interviews assigned yet</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {(() => {
                  // Helper function to get score from response
                  const getScore = (response) => {
                    if (response.overallFeedback?.overallScore !== undefined) {
                      return response.overallFeedback.overallScore;
                    }
                    const responses = response.responses || [];
                    const scoredResponses = responses.filter(r => r.aiAssessment?.score !== undefined);
                    if (scoredResponses.length > 0) {
                      return scoredResponses.reduce((sum, r) => sum + r.aiAssessment.score, 0) / scoredResponses.length;
                    }
                    return null;
                  };

                  // Helper function to render score badge
                  const renderScoreBadge = (score, small = false) => {
                    if (score === null) {
                      return (
                        <span style={{ 
                          color: '#a0aec0', 
                          fontSize: small ? '11px' : '13px',
                          fontStyle: 'italic' 
                        }}>
                          -
                        </span>
                      );
                    }
                    return (
                      <span style={{
                        fontWeight: '700',
                        fontSize: small ? '13px' : '15px',
                        color: getScoreColor(score)
                      }}>
                        {score.toFixed(0)}
                      </span>
                    );
                  };

                  // Calculate max attempts to determine number of columns
                  const maxAttempts = Math.max(...interviewResponses.map(group => group.attempts.length));

                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568', minWidth: '200px' }}>
                            Interview Name
                          </th>
                          {[...Array(maxAttempts)].map((_, i) => (
                            <th key={i} style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568', minWidth: '100px' }}>
                              Attempt {i + 1}
                            </th>
                          ))}
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568', minWidth: '100px', backgroundColor: '#edf2f7' }}>
                            Overall Avg
                          </th>
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {interviewResponses.map((group, groupIndex) => (
                          <tr
                            key={groupIndex}
                            style={{
                              borderBottom: '1px solid #e2e8f0',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <td style={{ padding: '12px', fontSize: '13px', color: '#2d3748', fontWeight: '600' }}>
                              <div>{group.interview.title}</div>
                              <div style={{ fontSize: '11px', color: '#718096', fontWeight: '400', marginTop: '4px' }}>
                                {group.attempts.length} attempt{group.attempts.length > 1 ? 's' : ''}
                              </div>
                            </td>
                            {[...Array(maxAttempts)].map((_, attemptIndex) => {
                              const attempt = group.attempts[attemptIndex];
                              if (!attempt) {
                                return (
                                  <td key={attemptIndex} style={{ padding: '12px', textAlign: 'center' }}>
                                    <span style={{ color: '#e2e8f0', fontSize: '20px' }}>-</span>
                                  </td>
                                );
                              }
                              const score = getScore(attempt);
                              return (
                                <td key={attemptIndex} style={{ padding: '12px', textAlign: 'center' }}>
                                  <div style={{ marginBottom: '4px' }}>
                                    {renderScoreBadge(score, true)}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#718096' }}>
                                    {getStatusBadge(attempt.status)}
                                  </div>
                                  {attempt.submittedAt && (
                                    <div style={{ fontSize: '10px', color: '#a0aec0', marginTop: '2px' }}>
                                      {new Date(attempt.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ padding: '12px', textAlign: 'center', backgroundColor: '#f7fafc' }}>
                              {(() => {
                                const scores = group.attempts.map(a => getScore(a)).filter(s => s !== null);
                                if (scores.length === 0) {
                                  return <span style={{ color: '#a0aec0', fontSize: '13px' }}>-</span>;
                                }
                                const avgScore = scores.reduce((sum, s) => sum + s, 0) / scores.length;
                                return renderScoreBadge(avgScore);
                              })()}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <button
                                onClick={() => navigate(`/admin/interviews/${group.interview._id}/responses`)}
                                style={{
                                  padding: '6px 12px',
                                  background: 'white',
                                  color: '#667eea',
                                  border: '1px solid #667eea',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                View All
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'assignments' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Coding Assignments
            </h3>
            
            {codingSubmissions.length === 0 ? (
              <div style={{
                padding: '48px 24px',
                textAlign: 'center',
                background: '#f7fafc',
                borderRadius: '12px',
                border: '2px dashed #cbd5e0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>💻</div>
                <p style={{ fontSize: '16px', color: '#718096', marginBottom: '8px' }}>
                  No coding assignments yet
                </p>
                <p style={{ fontSize: '14px', color: '#a0aec0' }}>
                  This student hasn't started any coding assignments
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Summary Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px' }}>
                      {assignmentStats.total}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Assignments</div>
                  </div>
                  
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px' }}>
                      {assignmentStats.completed}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Completed</div>
                  </div>
                  
                  <div style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '12px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px' }}>
                      {assignmentStats.incomplete}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>In Progress</div>
                  </div>
                </div>

                {/* Assignments Table */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Assignment
                        </th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Status
                        </th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Progress
                        </th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Started
                        </th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Last Updated
                        </th>
                        <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {codingSubmissions.map((submission) => {
                        // Calculate completion percentage based on total steps in assignment
                        const totalSteps = submission.assignment?.totalSteps || 0;
                        const completedSteps = submission.stepSubmissions?.filter(s => s.isComplete).length || 0;
                        const progressPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

                        return (
                          <tr key={submission._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                  {submission.assignment?.title || 'Untitled Assignment'}
                                </div>
                                {submission.assignment?.assignmentCode && (
                                  <div style={{ fontSize: '12px', color: '#718096' }}>
                                    Code: {submission.assignment.assignmentCode}
                                  </div>
                                )}
                                {submission.assignment?.tags && submission.assignment.tags.length > 0 && (
                                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                                    {submission.assignment.tags.map(tag => (
                                      <span
                                        key={tag._id}
                                        style={{
                                          padding: '2px 8px',
                                          background: '#edf2f7',
                                          color: '#4a5568',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          fontWeight: '500'
                                        }}
                                      >
                                        {tag.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <span style={{
                                padding: '6px 12px',
                                background: submission.isCompleted ? '#c6f6d5' : '#fed7d7',
                                color: submission.isCompleted ? '#22543d' : '#742a2a',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: 'inline-block'
                              }}>
                                {submission.isCompleted ? '✓ Completed' : '⏳ In Progress'}
                              </span>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{
                                  width: '100%',
                                  height: '8px',
                                  background: '#e2e8f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    background: progressPercent === 100 ? '#48bb78' : '#667eea',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                                <div style={{ fontSize: '12px', color: '#4a5568', fontWeight: '500' }}>
                                  {progressPercent}% • {completedSteps}/{totalSteps} steps
                                  {completedSteps === totalSteps && !submission.isCompleted && (
                                    <span style={{ color: '#f59e0b', marginLeft: '6px' }}>
                                      (quizzes pending)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '16px', fontSize: '13px', color: '#4a5568' }}>
                              {new Date(submission.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </td>
                            <td style={{ padding: '16px', fontSize: '13px', color: '#4a5568' }}>
                              {new Date(submission.updatedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </td>
                            <td style={{ padding: '16px', textAlign: 'center' }}>
                              <button
                                onClick={() => navigate(`/assignments/${submission.assignment?._id}/submissions`)}
                                style={{
                                  padding: '6px 14px',
                                  background: '#667eea',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#5568d3'}
                                onMouseOut={(e) => e.target.style.background = '#667eea'}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
              Account Details
            </h3>
            
            {student && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden'
              }}>
                {/* Account Information */}
                <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '16px' }}>
                    Account Information
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Full Name</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{student.name}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Email Address</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{student.email}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>User ID</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', fontFamily: 'monospace' }}>
                        {student._id}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Role</div>
                      <div>
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: student.role === 'admin' ? '#c6f6d5' : '#bee3f8',
                          color: student.role === 'admin' ? '#22543d' : '#2c5282',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {student.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '16px' }}>
                    Account Status
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Status</div>
                      <div>
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: student.isActive !== false ? '#c6f6d5' : '#fed7d7',
                          color: student.isActive !== false ? '#22543d' : '#742a2a',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {student.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Email Verified</div>
                      <div>
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: student.isEmailVerified ? '#c6f6d5' : '#feebc8',
                          color: student.isEmailVerified ? '#22543d' : '#744210',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {student.isEmailVerified ? '✓ Verified' : '⚠ Not Verified'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Joined</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                        {new Date(student.createdAt).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Last Updated</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                        {student.updatedAt ? new Date(student.updatedAt).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin Actions */}
                <div style={{ padding: '24px', background: '#f7fafc' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '16px' }}>
                    Admin Actions
                  </h4>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        if (window.confirm(`Send account details to ${student.name}?\n\nA new password will be generated and account details will be sent to ${student.email}.`)) {
                          handleSendAccountDetails();
                        }
                      }}
                      disabled={sendingAccountDetails}
                      style={{
                        padding: '10px 20px',
                        background: sendingAccountDetails ? '#9ca3af' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: sendingAccountDetails ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                        opacity: sendingAccountDetails ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!sendingAccountDetails) {
                          e.currentTarget.style.background = '#059669';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!sendingAccountDetails) {
                          e.currentTarget.style.background = '#10b981';
                        }
                      }}
                    >
                      {sendingAccountDetails ? '📧 Sending...' : '📧 Send Account Details'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm(`Reset password for ${student.name}?\n\nA new temporary password will be generated and sent to ${student.email}.`)) {
                          handlePasswordReset();
                        }
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#5568d3'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#667eea'}
                    >
                      🔑 Reset Password
                    </button>
                    
                    <button
                      onClick={handleToggleUserStatus}
                      disabled={togglingStatus}
                      style={{
                        padding: '10px 20px',
                        background: student.isActive !== false ? '#f6ad55' : '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: togglingStatus ? 'not-allowed' : 'pointer',
                        opacity: togglingStatus ? 0.6 : 1,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!togglingStatus) {
                          e.currentTarget.style.opacity = '0.8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!togglingStatus) {
                          e.currentTarget.style.opacity = '1';
                        }
                      }}
                    >
                      {togglingStatus ? 'Processing...' : (student.isActive !== false ? '⏸ Deactivate Account' : '▶️ Activate Account')}
                    </button>
                    
                    <button
                      onClick={handleDeleteUser}
                      disabled={deleting}
                      style={{
                        padding: '10px 20px',
                        background: '#fc8181',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: deleting ? 'not-allowed' : 'pointer',
                        opacity: deleting ? 0.6 : 1,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!deleting) {
                          e.currentTarget.style.background = '#e53e3e';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!deleting) {
                          e.currentTarget.style.background = '#fc8181';
                        }
                      }}
                    >
                      {deleting ? 'Deleting...' : '🗑 Delete Account'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentProfile;
